/**
 * Upload protocol constants (docs/requirements/02-upload-ingestion.md §2–3, server spec §3.1).
 */

export const MiB = 1024 * 1024;
export const GiB = 1024 * MiB;

/** Hard limit per file: 50 GiB. */
export const MAX_FILE_BYTES = 50 * GiB;
/** S3 multipart ceiling. */
export const MAX_PARTS = 10_000;
/** Files above this use the sampled hash (first 8 MiB + last 8 MiB + size). */
export const SAMPLED_HASH_THRESHOLD_BYTES = 2 * GiB;
export const SAMPLED_HASH_HEAD_BYTES = 8 * MiB;
export const SAMPLED_HASH_TAIL_BYTES = 8 * MiB;
/** Streaming read chunk for the Web Worker SHA-256. */
export const HASH_READ_CHUNK_BYTES = 64 * MiB;
/** Server re-verifies SHA-256 only up to this size. */
export const HASH_VERIFY_MAX_BYTES = 1 * GiB;

/** 8 MiB for files < 1 GiB, 16 MiB otherwise. */
export const PART_SIZE_SMALL = 8 * MiB;
export const PART_SIZE_LARGE = 16 * MiB;
export const PART_SIZE_THRESHOLD_BYTES = 1 * GiB;

/** At most 3 files uploading simultaneously … */
export const MAX_CONCURRENT_FILES = 3;
/** … and at most 4 parts in flight per file (≤ 12 PUTs total). */
export const MAX_PARTS_IN_FLIGHT = 4;
/** Presigned URLs returned in the initiate response; the rest via `/parts/refresh`. */
export const INITIAL_PRESIGNED_PARTS = 1000;
export const MAX_PARTS_PER_REFRESH = 1000;
/** Presigned PUT validity (6 h). */
export const PRESIGN_UPLOAD_TTL_SECONDS = 6 * 3600;
/** Upload sessions are abandoned (and aborted) after 24 h without completion. */
export const UPLOAD_SESSION_TTL_HOURS = 24;
/** Guest upload bearer validity. */
export const GUEST_UPLOAD_TOKEN_TTL_SECONDS = 6 * 3600;

/** Per-part retry policy: `min(30s, 1s * 2^attempt) + jitter(0–500ms)`, max 6 attempts. */
export const PART_MAX_ATTEMPTS = 6;
export const PART_BACKOFF_BASE_MS = 1000;
export const PART_BACKOFF_MAX_MS = 30_000;
export const PART_BACKOFF_JITTER_MS = 500;

/** Server-side part size rule (02 §3.1). */
export function partSizeFor(sizeBytes: number): number {
  return sizeBytes < PART_SIZE_THRESHOLD_BYTES ? PART_SIZE_SMALL : PART_SIZE_LARGE;
}

/** Part count for a file; files ≤ part_size are single-part. */
export function partCountFor(sizeBytes: number, partSize: number = partSizeFor(sizeBytes)): number {
  if (sizeBytes <= 0) throw new RangeError('size must be > 0');
  return Math.max(1, Math.ceil(sizeBytes / partSize));
}

/** Byte range `[start, end)` of 1-based part `n`. */
export function partRange(
  partNumber: number,
  sizeBytes: number,
  partSize: number = partSizeFor(sizeBytes),
): { start: number; end: number } {
  const start = (partNumber - 1) * partSize;
  return { start, end: Math.min(start + partSize, sizeBytes) };
}

export function hashKindFor(sizeBytes: number): 'full' | 'sampled' {
  return sizeBytes > SAMPLED_HASH_THRESHOLD_BYTES ? 'sampled' : 'full';
}

/**
 * Delay before retry `attempt` (0-based). Pass `random` for deterministic tests.
 * 403-expired URLs are refreshed and do not consume an attempt (02 §3.2).
 */
export function partBackoffMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(PART_BACKOFF_MAX_MS, PART_BACKOFF_BASE_MS * 2 ** attempt);
  return base + Math.floor(random() * (PART_BACKOFF_JITTER_MS + 1));
}

export function shouldRetryPart(attempt: number): boolean {
  return attempt + 1 < PART_MAX_ATTEMPTS;
}
